import axios from 'axios';
import { runInAction } from 'mobx';
import { UserEntity } from 'Models/Entities';
import { appConfigStore } from 'Models/AppConfigStore';
import { isNotNullOrUndefined, stringNotEmpty } from 'Util/TypeGuards';

/**
 * Whether the company name belongs on this account's fields.
 *
 * Two independent sources put it there. The customer type is the primary one — staff assign a type
 * with companyNameFieldEnabled and the field appears for every customer of that type. The
 * registration config is the secondary one: a customer who ticked "Register as a company" has a
 * company name but no business type, so without this second gate their name would be hidden here
 * and then wiped by saveUser below on their next save.
 *
 * Pass `staffView` on the staff user edit screen, where the config alone is enough: staff need to
 * be able to add a company to an account that has none. Customers only ever see the field for a
 * company they already have, so the option isn't advertised to everyone registering personally.
 */
export function showCompanyNameField(
	displayName: { companyNameFieldEnabled?: boolean } | null | undefined,
	companyName: string | null | undefined,
	options?: { staffView?: boolean },
): boolean {
	if (displayName?.companyNameFieldEnabled ?? false) {
		return true;
	}
	if (!appConfigStore.userCompanyNameEnabled) {
		return false;
	}
	return (options?.staffView ?? false) || stringNotEmpty(companyName);
}

export async function saveUser(user: UserEntity, originalUser: UserEntity, isManager: boolean) {
	// Copy the user so the (possibly null) prices stay visible in the UI — we only shape what's
	// sent to the server.
	const copiedUser = new UserEntity(user);
	runInAction(() => {
		copiedUser.prices = copiedUser.prices?.filter(x => isNotNullOrUndefined(x.amount));
		copiedUser.addOnsPrices = copiedUser.addOnsPrices?.filter(x => isNotNullOrUndefined(x.price));
		copiedUser.additionalInformation = copiedUser.additionalInformation ?? undefined;
		if (!(copiedUser?.userDisplayName?.additionalInformationEnabled ?? false)) {
			copiedUser.additionalInformation = undefined;
		}
		if (!showCompanyNameField(copiedUser?.userDisplayName, copiedUser.companyName)) {
			copiedUser.companyName = undefined;
		}
	});

	// A single endpoint replaces the old "delete each removed/cleared price, then save the user"
	// flow. The server hard-deletes the orphaned price / add-on rows and upserts the survivors in
	// one transaction (see UserEntityController.SaveAccount), so there are no client-side delete
	// races. Pricing is only saved for managers.
	await axios.put(
		`/api/entity/UserEntity/save-account?includePricing=${isManager}`,
		copiedUser.toJSON({ prices: {}, addOnsPrices: {} }),
	);

	return copiedUser;
}

export const password = {
	password: '************',
};
